import { defineStore } from "pinia";
import userApi from "@/services/api/userApi";
import { AxiosResponse } from "axios";
import { useUserStore } from "@/store/user";
import router from "@/router";
import { useFreeTrialContext } from "@/composables/useFreeTrialContext";

export const useAuthStore = defineStore(
  "auth",
  () => {
    const userStore = useUserStore();

    function refreshToken(): Promise<AxiosResponse> {
      return userApi
        .refreshToken({ refreshToken: userStore.refreshToken || "" })
        .then((response) => {
          const { accessToken: _accessToken, refreshToken: _refreshToken } =
            response.data.data;
          userStore.setTokens({
            accessToken: _accessToken,
            refreshToken: _refreshToken,
          });
          return response.data.data;
        })
        .catch(() => {
          userStore.clear();
          router.push("/login");
        });
    }

    function login(payload: {
      password: string;
      email: string;
      type?: string;
    }): Promise<AxiosResponse> {
      return userApi.logIn(payload).then((res) => {
        if (!payload.type) {
          userStore.setTokens({
            accessToken: res.data.data.accessToken,
            refreshToken: res.data.data.refreshToken,
          });
          userStore.data = res.data.data.user;
        }
        return res;
      });
    }

    function loginWithToken(payload: {
      accessToken: string;
      refreshToken: string;
    }) {
      userStore.setTokens(payload);
      return userStore.getMe().then((res) => {
        return res;
      });
    }

    function logout(): Promise<AxiosResponse> {
      return userApi.logOut().then((res) => {
        userStore.clear();
        return res;
      });
    }

    function signup(
      payload: {
        password: string;
        passwordRepeat: string;
        email: string;
        promoCode?: string;
        termsId: string;
      },
      isTrialSignup = false
    ): Promise<AxiosResponse> {
      const { clear, activate } = useFreeTrialContext();
      clear();
      return userApi.signUp(payload).then((res) => {
        if (isTrialSignup) {
          activate();
        }
        return res;
      });
    }

    return {
      refreshToken,
      login,
      loginWithToken,
      logout,
      signup,
    };
  },
  {
    persist: true,
  }
);
